import React, { createContext, useCallback, useMemo, useRef, useState, useEffect, ReactNode } from 'react';

export interface HeadTags {
    title: string;
    meta: string;
    link: string;
}

const defaultHeadTags: HeadTags = {
    title: '',
    meta: '',
    link: '',
};

type HeadTagsRef = React.MutableRefObject<HeadTags>;
type SetHeadTags = (partial: Partial<HeadTags>) => void;

type HeadTagsApi = {
    setHead: SetHeadTags;
    resetHead: () => void;
    ref: HeadTagsRef;
};

/**
 * Consumers (pages calling setHead) must NOT subscribe to `version`.
 * Putting version in the same context re-rendered every setHead caller; if a
 * caller also had unstable useEffect deps (new object each render), that
 * produced an infinite update loop and blanked the app on SPA navigations.
 */
const HeadTagsApiContext = createContext<HeadTagsApi | null>(null);
const HeadTagsVersionContext = createContext(0);

const TAG_PATTERN = /<(?:meta|link)\b[^>]*>/gi;

function tagKey(tag: string): string {
    const attr = (name: string) => new RegExp(`\\s${name}="([^"]*)"`, 'i').exec(tag)?.[1];
    if (/^<link/i.test(tag) && attr('rel') === 'canonical') return 'link:canonical';
    const name = attr('name') ?? attr('property');
    return /^<meta/i.test(tag) && name ? `meta:${name}` : tag;
}

export function appendHeadTags(existing: string, incoming: string): string {
    const next = incoming.match(TAG_PATTERN) ?? [];
    const replaced = new Set(next.map(tagKey));
    const kept = (existing.match(TAG_PATTERN) ?? []).filter((tag) => !replaced.has(tagKey(tag)));
    return [...kept, ...next].join('');
}

function mergeHeadTags(ref: HeadTagsRef, partial: Partial<HeadTags>): boolean {
    let changed = false;
    if (partial.title !== undefined && ref.current.title !== partial.title) {
        ref.current.title = partial.title;
        changed = true;
    }
    // Append so AppShell Head + page-level tags can compose (legacy Helmet merge).
    if (partial.meta !== undefined) {
        const next = appendHeadTags(ref.current.meta || '', partial.meta);
        if (ref.current.meta !== next) {
            ref.current.meta = next;
            changed = true;
        }
    }
    if (partial.link !== undefined) {
        const next = appendHeadTags(ref.current.link || '', partial.link);
        if (ref.current.link !== next) {
            ref.current.link = next;
            changed = true;
        }
    }
    return changed;
}

export function HeadTagsProvider({ children, headRef }: { children: ReactNode; headRef?: HeadTagsRef }) {
    const localRef = useRef<HeadTags>({ ...defaultHeadTags });
    const ref = headRef ?? localRef;
    const [version, setVersion] = useState(0);

    if (typeof window === 'undefined') {
        ref.current = { ...defaultHeadTags };
    }

    const setHead = useCallback<SetHeadTags>(
        (partial) => {
            if (mergeHeadTags(ref, partial)) setVersion((v) => v + 1);
        },
        [ref],
    );

    const resetHead = useCallback(() => {
        ref.current = { ...defaultHeadTags };
    }, [ref]);

    const api = useMemo<HeadTagsApi>(() => ({ setHead, resetHead, ref }), [setHead, resetHead, ref]);

    return (
        <HeadTagsApiContext.Provider value={api}>
            <HeadTagsVersionContext.Provider value={version}>{children}</HeadTagsVersionContext.Provider>
        </HeadTagsApiContext.Provider>
    );
}

export function useHeadTags(): HeadTagsApi {
    const ctx = React.useContext(HeadTagsApiContext);
    if (!ctx) {
        return {
            setHead: () => {},
            resetHead: () => {},
            ref: { current: { ...defaultHeadTags } },
        };
    }
    return ctx;
}

export function useHead(tags: Partial<HeadTags>) {
    const { setHead, ref } = useHeadTags();
    if (typeof window === 'undefined') {
        mergeHeadTags(ref, tags);
    }
    const { title, meta, link } = tags;
    useEffect(() => {
        setHead({ title, meta, link });
    }, [setHead, title, meta, link]);
}

export const SSR_HEAD_ATTR = 'data-ssr-head';

export function markSsrHeadTags(html: string): string {
    return html.replace(/<(title|meta|link)\b/g, `<$1 ${SSR_HEAD_ATTR}`);
}

/**
 * Client-only: injects ref.current (title, meta, link HTML strings) into document.head.
 * Server uses getHelmet() which reads the ref directly.
 */
export function HeadTagsInjector() {
    const api = React.useContext(HeadTagsApiContext);
    const version = React.useContext(HeadTagsVersionContext);
    const [headHtml, setHeadHtml] = useState<HeadTags>(() => (api ? { ...api.ref.current } : { ...defaultHeadTags }));

    useEffect(() => {
        if (!api) return;
        setHeadHtml({ ...api.ref.current });
    }, [api, version]);

    useEffect(() => {
        if (!headHtml.title && !headHtml.meta && !headHtml.link) return;
        const wrap = document.createElement('div');
        wrap.innerHTML = (headHtml.title || '') + (headHtml.meta || '') + (headHtml.link || '');
        const nodes = Array.from(wrap.children);
        document.head.querySelectorAll(`[${SSR_HEAD_ATTR}]`).forEach((el) => el.remove());
        nodes.forEach((el) => document.head.appendChild(el));
        return () => nodes.forEach((el) => el.remove());
    }, [headHtml.title, headHtml.meta, headHtml.link]);

    useEffect(() => {
        if (!headHtml.title) return;
        const inner = /<title[^>]*>([^<]*)<\/title>/i.exec(headHtml.title);
        if (inner) document.title = inner[1];
    }, [headHtml.title]);

    return null;
}
